import { Box, Button, Flex, Skeleton, Text } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '@/components/ui';
import { PullToRefresh } from '@/components/common/PullToRefresh';
import {
  NotificationRow,
  NotificationSection,
  useRowRemoval,
} from '@/components/common/NotificationRow';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/constants/UserContext';
import {
  useNotifications,
  type AppNotification,
} from '@/hooks/useNotifications';
import { getNotificationNavigationUrl } from '@/utils/notificationNavigation';
import {
  NOTIFICATION_FILTERS,
  filterNotifications,
  groupNotifications,
  type NotificationFilter,
} from '@/utils/notificationGrouping';
import { NotificationsHeader } from './components/NotificationsHeader';
import { FilterChips } from './components/FilterChips';
import { DeleteAllSheet } from './components/DeleteAllSheet';
import { NotificationsEmptyState } from './components/NotificationsEmptyState';

export function NotificationsPage() {
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading } = useAuth();
  const { user: profileUser } = useUser();

  // Inbox is keyed by auth uid (same as profiles.id). UserContext can lag behind session — don't fetch until auth is ready.
  const recipientId = authUser?.id;
  const {
    notifications,
    loading,
    error,
    hasMore,
    loadMore,
    loadingMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refetch,
  } = useNotifications(recipientId, authLoading);

  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);

  // Viewing the list reads nothing, the same rule as the bell panel: a row stays unread
  // until it is tapped, or until "Mark all as read" in the header.
  const isOrgAccount = profileUser?.role === 'organization';

  // Deleted rows stay on screen until their exit animation has played.
  const { rows, exitingIds, removeAnimated } = useRowRemoval(
    notifications,
    deleteNotification
  );
  const newCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );
  const groups = useMemo(
    () => groupNotifications(filterNotifications(rows, filter)),
    [rows, filter]
  );
  const filterEmptyText = NOTIFICATION_FILTERS.find(
    (f) => f.key === filter
  )?.emptyText;

  const activateNotification = useCallback(
    async (n: AppNotification) => {
      // While a row is swiped open, the first tap anywhere just closes it.
      if (openSwipeId) {
        setOpenSwipeId(null);
        return;
      }
      if (!n.read) await markAsRead(n.id);
      const url = getNotificationNavigationUrl(n);
      if (url) navigate(url);
    },
    [openSwipeId, markAsRead, navigate]
  );

  const handleDeleteOne = useCallback(
    async (id: string) => {
      // The row animates out at once; if the delete fails it comes back.
      await removeAnimated(id);
      setOpenSwipeId(null);
    },
    [removeAnimated]
  );

  const handleDeleteAll = useCallback(() => {
    setDeleteAllOpen(false);
    void deleteAllNotifications();
  }, [deleteAllNotifications]);

  const closeDeleteAll = useCallback(() => setDeleteAllOpen(false), []);

  const handleFilterChange = useCallback((next: NotificationFilter) => {
    setFilter(next);
    setOpenSwipeId(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // rows, not notifications: the last row still animates out before the empty state.
  const hasNotifications = !loading && !error && rows.length > 0;

  return (
    <Box
      minH="100vh"
      bg="volyx.lightGray"
      pl={{ base: 0, lg: 'var(--sidebar-width)' }}
      pb={{ base: 'var(--mobile-bottom-safe)', lg: '24px' }}
      transition="padding-left 0.3s ease-in-out"
    >
      <PullToRefresh onRefresh={handleRefresh}>
        <Box
          maxW="720px"
          w="100%"
          mx="auto"
          pl={{ base: 'max(16px, env(safe-area-inset-left, 0px))', md: 5 }}
          pr={{ base: 'max(16px, env(safe-area-inset-right, 0px))', md: 5 }}
          pt={{ base: 2, lg: 10 }}
          pb={{ base: 4, lg: 6 }}
        >
          <NotificationsHeader
            newCount={hasNotifications ? newCount : 0}
            showMore={hasNotifications}
            onBack={() => navigate('/organizations')}
            onMore={() => setDeleteAllOpen(true)}
            onMarkAllRead={markAllAsRead}
          />

          {loading && (
            <Box
              mt="6"
              bg="white"
              borderRadius="2xl"
              overflow="hidden"
              aria-busy="true"
            >
              {[0, 1, 2].map((k) => (
                <Flex key={k} gap="3" px="4" py="3.5">
                  <Skeleton
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    flexShrink={0}
                  />
                  <Box flex="1">
                    <Skeleton h="14px" w="60%" borderRadius="md" />
                    <Skeleton h="12px" w="90%" mt="2.5" borderRadius="md" />
                  </Box>
                </Flex>
              ))}
            </Box>
          )}

          {!loading && error && (
            <Text
              mt={{ base: 4, md: 6 }}
              color="red.500"
              fontSize="sm"
              wordBreak="break-word"
            >
              {error}
            </Text>
          )}

          {!loading && !error && rows.length === 0 && (
            <NotificationsEmptyState
              isOrgAccount={isOrgAccount}
              onRefresh={() => void refetch()}
            />
          )}

          {hasNotifications && (
            <>
              <Box mt={{ base: 4, lg: 5 }}>
                <FilterChips value={filter} onChange={handleFilterChange} />
              </Box>
              <Flex direction="column" mt="5">
                {groups.length === 0 ? (
                  <Text
                    m="0"
                    py="10"
                    textAlign="center"
                    fontSize="sm"
                    color="volyx.textSecondary"
                  >
                    {filterEmptyText}
                  </Text>
                ) : (
                  groups.map((g) => (
                    <NotificationSection
                      key={g.key}
                      label={g.label}
                      collapsing={g.items.every((n) => exitingIds.has(n.id))}
                    >
                      {g.items.map((n) => (
                        <NotificationRow
                          key={n.id}
                          notification={n}
                          onActivate={(row) => void activateNotification(row)}
                          onDelete={(id) => void handleDeleteOne(id)}
                          swipeOpen={openSwipeId === n.id}
                          exiting={exitingIds.has(n.id)}
                          onSwipeOpenChange={(open) =>
                            setOpenSwipeId((cur) =>
                              open ? n.id : cur === n.id ? null : cur
                            )
                          }
                        />
                      ))}
                    </NotificationSection>
                  ))
                )}
              </Flex>
              {/* The query is paged (50 at a time); without this the older history
                  would be unreachable rather than merely slow to load. Shown under a
                  filter too, since older matches may sit on the next page. */}
              {hasMore && (
                <Flex justify="center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    bg="white"
                    borderColor="volyx.borderLight"
                    disabled={loadingMore}
                    onClick={() => void loadMore()}
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </Button>
                </Flex>
              )}
            </>
          )}
        </Box>
      </PullToRefresh>

      <DeleteAllSheet
        open={deleteAllOpen}
        onConfirm={handleDeleteAll}
        onClose={closeDeleteAll}
      />
      <NavBar />
    </Box>
  );
}
